import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { WarningMessage, EarlyAccessMessage } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { AuthService } from 'app/services/auth.service';
import { Timeout } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Company from 'entity/Company';
import Social from 'entity/Social';
import SocialRoomSet from 'entity/SocialRoomSet';
import User from 'entity/User';

import { BrowserService } from '../services/browser.service';
import { GroupShareScreenParticipantService } from '../services/share-screen-group/group-share-screen-participant.service';
import { TimerService } from '../services/timer.service';
import { socialAppSelectors } from '../state/app';

@Component({
  selector: 'app-social-header',
  templateUrl: './social-header.component.html',
  styleUrls: ['./social-header.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialHeaderComponent extends Loggable implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private timerService = inject(TimerService);
  private browserService = inject(BrowserService);
  private store = inject(Store);
  private groupShareParticipantService = inject(GroupShareScreenParticipantService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() public social?: Social;
  private _inSocial = false;
  protected close = false;
  protected isGuest = false;
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  public get inSocial(): boolean {
    return this._inSocial;
  }
  public set inSocial(value: boolean) {
    this._inSocial = value;
  }
  public readonly company = input<Company>(); // host company
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() public set user(value: User | undefined) {
    this._user = value;
    this.isGuest = this.authService.isAnonymous();
  }
  public get user(): User | undefined {
    return this._user;
  }
  private _user?: User;
  public readonly isHost = input(false);

  public readonly trigger = viewChild(MatMenuTrigger);

  public connectedRoomSet?: SocialRoomSet;
  public socialHasLoaded?: boolean;
  public userCompany?: Company; // logged in user company
  public shouldShowWarning = false;
  public warningMessage?: WarningMessage;
  public earlyAccessMessage?: EarlyAccessMessage;
  public displayEarlyAccess = false;
  public componentJustStarted = true;
  public isTimerVisible = false;
  public userIsInteracting = false;
  public windowWidth = 0;
  @HostListener('window:resize', ['$event'])
  private onResize() {
    this.windowWidth = window.innerWidth;
  }

  private userIsInteractingTimeout?: Timeout;
  private movementListenerBlocked = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  public isPresentationActive = false;
  public readonly groupParticipantScreenShareActive = computed((): boolean => {
    return this.groupShareParticipantService.isGroupShareActiveSignal();
  });
  public isMobile = false;

  public async ngOnInit() {
    // Company
    this.getCompany();
    this.onResize();

    const check = await this.browserService.checkBrowserAvailability();
    this.isMobile = this.browserService.isMobile();
    this.displayEarlyAccess = check.isAvailable && check.earlyAccess;

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((social) => {
        this.social = social;
        this.changeDetectorRef.markForCheck();
      });

    // Should Display Timer?
    this.timerService.didReachTargetTime.pipe(takeUntil(this.destroyed$)).subscribe((visible) => {
      this.isTimerVisible = visible && notNil(this.social?.endDateTime);
      this.changeDetectorRef.markForCheck();
    });

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomSet)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((connectedRoomSet) => {
        this.connectedRoomSet = connectedRoomSet;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getSocialSharedState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.socialHasLoaded = !!state;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        this.isPresentationActive = isPresentationActive;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_error_banner)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((warningMessage) => {
        this.warningMessage = warningMessage;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_early_access_banner)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((earlyAccessMessage) => {
        this.earlyAccessMessage = earlyAccessMessage;
        this.changeDetectorRef.markForCheck();
      });
  }

  @HostListener('window:keydown')
  @HostListener('window:click')
  @HostListener('window:mousemove')
  private movementListener = () => {
    // Blocked while animation
    if (this.movementListenerBlocked) {
      return;
    }
    // clean flag when movement listener is executed for the first time
    this.componentJustStarted = false;
    //
    this.userIsInteracting = true;
    if (this.userIsInteractingTimeout) {
      clearTimeout(this.userIsInteractingTimeout);
    }
    this.userIsInteractingTimeout = setTimeout(() => {
      this.userIsInteracting = false;

      // Block while animation
      this.movementListenerBlocked = true;
      setTimeout(() => {
        this.movementListenerBlocked = false;
        this.changeDetectorRef.markForCheck();
      }, 500);
    }, 5000);
  };

  public getCompany(): void {
    this.authService.getCurrentCompany().then(
      (res) => {
        this.userCompany = res;
      },
      () => {
        this.userCompany = undefined;
      },
    );
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public openMenu(_event: Event): void {
    this.trigger()?.openMenu();
  }

  public signOut(): void {
    this.authService
      .signOutUser()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.router.navigate(['welcome']).catch((err) => {
          this.error(err);
        });
      });
  }
}
